import { defineStore } from "pinia";
import { onMounted, ref } from "vue";
import {
  fetchEpdProductIndustryType,
  fetchEpdSpecificationForm,
  fetchIndicatorEnums,
  fetchMassUnitEnum,
  fetchIndicatorTypeNames,
  fetchTagsTypesForms,
  fetchDeclaredUnitEnums,
  fetchStageTypeEnums,
} from "../../api/services/Materials";
import handleApiError from "../../services/Utils/ErrorHandler";
import { sortTags } from "../../services/Views/MaterialDatabase/TagsSort";

export interface IdendityEnum {
  Enum: string;
  DisplayName: string;
  index?: number;
}

export interface KeyedIdentityEnum {
  [key: string]: IdendityEnum;
}

export interface KeyedStringEnum {
  [key: string]: string;
}

interface LibraryEnumsProvider {
  getDeclaredUnitEnums: () => Promise<KeyedIdentityEnum | undefined>;
  getMassUnitEnums: () => Promise<KeyedIdentityEnum | undefined>;
  getStageTypeEnums: () => Promise<KeyedIdentityEnum | undefined>;
  getSpecificationFormEnums: () => Promise<KeyedIdentityEnum | undefined>;
  getProductIndustryTypeEnums: () => Promise<KeyedIdentityEnum | undefined>;
  getIndicatorEnums: () => Promise<KeyedStringEnum | undefined>;
  getIndicatorTypeEnums: () => Promise<KeyedStringEnum | undefined>;
  getTagList: () => Promise<string[] | undefined>;
}

const useLibraryEnums = defineStore("LibraryEnumsProvider", (): LibraryEnumsProvider => {
  //helpers
  const isPopulated = ref<boolean>(false);
  const initializeTaskList: Promise<number>[] = [];

  //states
  const declaredUnitEnums = ref<KeyedIdentityEnum | undefined>();
  const massUnitEnums = ref<KeyedIdentityEnum | undefined>();
  const stageTypeEnums = ref<KeyedIdentityEnum | undefined>();
  const specificationFormEnums = ref<KeyedIdentityEnum | undefined>();
  const productIndustryTypeEnums = ref<KeyedIdentityEnum | undefined>();
  const indicatorEnums = ref<KeyedStringEnum | undefined>();
  const indicatorTypeEnums = ref<KeyedStringEnum | undefined>();
  const tagList = ref<string[] | undefined>();

  //initialization tasks
  const initializeDeclaredUnitEnumss = (async () => {
    declaredUnitEnums.value = await fetchDeclaredUnitEnums().catch((err) => {
      throw err.response.status;
    });
    return 200;
  })();

  const initializeMassUnitEnumss = (async () => {
    massUnitEnums.value = await fetchMassUnitEnum().catch((err) => {
      throw err.response.status;
    });
    return 200;
  })();

  const initializeStageTypeEnums = (async () => {
    stageTypeEnums.value = await fetchStageTypeEnums().catch((err) => {
      throw err.response.status;
    });
    return 200;
  })();

  const initializeSpecificationFormEnums = (async () => {
    specificationFormEnums.value = await fetchEpdSpecificationForm().catch((err) => {
      throw err.response.status;
    });
    return 200;
  })();

  const initializeProductIndustryTypeEnums = (async () => {
    productIndustryTypeEnums.value = await fetchEpdProductIndustryType().catch((err) => {
      throw err.response.status;
    });
    return 200;
  })();

  const initializeIndicatorEnums = (async () => {
    indicatorEnums.value = await fetchIndicatorEnums().catch((err) => {
      throw err.response.status;
    });
    return 200;
  })();
  const initializeIndicatorTypeEnums = (async () => {
    indicatorTypeEnums.value = await fetchIndicatorTypeNames().catch((err) => {
      throw err.response.status;
    });
    return 200;
  })();
  const initializeTagList = (async () => {
    const result = await fetchTagsTypesForms().catch((err) => {
      throw err.response.status;
    });
    tagList.value = sortTags(result.tags);
    return 200;
  })();

  //initialize
  onMounted(async () => {
    if (isPopulated.value) return;
    initializeTaskList.push(initializeDeclaredUnitEnumss);
    initializeTaskList.push(initializeMassUnitEnumss);
    initializeTaskList.push(initializeStageTypeEnums);
    initializeTaskList.push(initializeSpecificationFormEnums);
    initializeTaskList.push(initializeProductIndustryTypeEnums);
    initializeTaskList.push(initializeIndicatorEnums);
    initializeTaskList.push(initializeIndicatorTypeEnums);
    initializeTaskList.push(initializeTagList);
    await Promise.all(initializeTaskList).catch((err) => {
      handleApiError(err);
    });
    if (declaredUnitEnums.value) isPopulated.value = true;
  });

  //getters
  const getDeclaredUnitEnums = async () => {
    if (isPopulated.value) return declaredUnitEnums.value;
    await initializeDeclaredUnitEnumss;
    return declaredUnitEnums.value;
  };
  const getMassUnitEnums = async () => {
    if (isPopulated.value) return massUnitEnums.value;
    await initializeMassUnitEnumss;
    return massUnitEnums.value;
  };
  const getStageTypeEnums = async () => {
    if (isPopulated.value) return stageTypeEnums.value;
    await initializeStageTypeEnums;
    return stageTypeEnums.value;
  };
  const getSpecificationFormEnums = async () => {
    if (isPopulated.value) return specificationFormEnums.value;
    await initializeSpecificationFormEnums;
    return specificationFormEnums.value;
  };
  const getProductIndustryTypeEnums = async () => {
    if (isPopulated.value) return productIndustryTypeEnums.value;
    await initializeProductIndustryTypeEnums;
    return productIndustryTypeEnums.value;
  };
  const getIndicatorEnums = async () => {
    if (isPopulated.value) return indicatorEnums.value;
    await initializeIndicatorEnums;
    return indicatorEnums.value;
  };
  const getIndicatorTypeEnums = async () => {
    if (isPopulated.value) return indicatorTypeEnums.value;
    await initializeIndicatorTypeEnums;
    return indicatorTypeEnums.value;
  };
  const getTagList = async () => {
    if (isPopulated.value) return tagList.value;
    await initializeTagList;
    return tagList.value;
  };

  //return getters, setters & actions
  return {
    getDeclaredUnitEnums,
    getMassUnitEnums,
    getStageTypeEnums,
    getSpecificationFormEnums,
    getProductIndustryTypeEnums,
    getIndicatorEnums,
    getIndicatorTypeEnums,
    getTagList,
  };
});

export default useLibraryEnums;
